import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import AppContext, { FAVORITES_URL_PARAM_FOLDER } from '../../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import '../../assets/css/gpx.css';
import { useMap } from 'react-leaflet';
import TrackLayerProvider from '../util/TrackLayerProvider';
import FavoritesManager, {
    FAVORITE_FILE_TYPE,
    HIDDEN_TRUE,
    findSelectedWpt,
    openFavoriteObj,
} from '../../manager/FavoritesManager';
import { isFavoriteFromSearch } from '../../manager/SearchManager';
import { isMarkerLayer } from '../util/LayerUtils';
import isEmpty from 'lodash-es/isEmpty';
import { clusterMarkers, addMarkerTooltip } from '../util/Clusterizer';
import {
    restoreOriginalIcon,
    hideMarkersUnderPin,
    toShape,
    SELECTED_PIN_SIZE,
    SELECTED_ICON_SIZE,
    applySelectedPin,
    resetSelectedPin,
} from '../util/MarkerSelectionService';
import { panToIfNeeded } from '../util/MapManager';
import { panToVisibleCenter } from './MapStateLayer';
import { useSelectMarkerOnMap } from '../../util/hooks/map/useSelectMarkerOnMap';
import MarkerOptions, {
    createPoiIcon,
    DEFAULT_ICON_SIZE,
    DEFAULT_WPT_COLOR,
    ICONS_PREFIX,
} from '../markers/MarkerOptions';
import { createLayeredPinIcon } from '../markers/SelectedPinMarker';
import useHashParams from '../../util/hooks/useHashParams';
import L from 'leaflet';
import { hexToRgba } from '../../util/ColorUtil';
import useZoomMoveMapHandlers from '../../util/hooks/map/useZoomMoveMapHandlers';
import { updateMarkerZIndex } from './ExploreLayer';
import { deleteAllFavoritesFromDB } from '../../context/FavoriteStorage';
import LoginContext from '../../context/LoginContext';
import { MENU_INFO_OPEN_SIZE, NAVIGATE_URL } from '../../manager/GlobalManager';
import { MARKER_Z_INDEX_MAIN } from '../util/ZIndexes';
import { NAVIGATION_OBJECT_TYPE_FAVORITE } from '../../manager/NavigationManager';

function getAddFavoritePinLatLng(addFavorite) {
    const { location, editWpt } = addFavorite;
    if (location) return { lat: location.lat, lng: location.lng };
    if (editWpt) return { lat: editWpt.latlon?.lat ?? editWpt.lat, lng: editWpt.latlon?.lon ?? editWpt.lon };
    return null;
}

export function filterPointsInBounds(points, map) {
    if (!map) return [];
    if (!Array.isArray(points) || points.length === 0) return [];

    const mapBounds = map.getBounds();

    return points.filter((point) => {
        if (point == null) return false;
        const pointLatLng = L.latLng(point.lat, point.lon);
        return mapBounds.contains(pointLatLng);
    });
}

export function processMarkers({ layer, markerLatLng, mainMarkers, secondaryMarkers, mainLayers, secondaryLayers }) {
    if (!layer.options.originalIcon) {
        layer.options.originalIcon = layer.options.icon;
    }

    const isMainMarker = mainMarkers.some((mainMarker) => {
        const mainLatLng = L.latLng(mainMarker.lat, mainMarker.lon);
        return mainLatLng.equals(markerLatLng);
    });

    const isSecondaryMarker = secondaryMarkers.some((secMarker) => {
        const secLatLng = L.latLng(secMarker.lat, secMarker.lon);
        return secLatLng.equals(markerLatLng);
    });

    if (isMainMarker) {
        layer.options.simple = false;
        restoreOriginalIcon(layer);
        mainLayers.push(layer);
    }

    if (isSecondaryMarker) {
        const color = layer.options.color ? hexToRgba(layer.options.color) : DEFAULT_WPT_COLOR;
        const customIcon = L.divIcon({
            className: 'custom-circle-icon',
            iconSize: [10, 10],
            html: `<div style="background-color:${color};border-radius:50%;width:10px;height:10px;border:1px solid #ffffff;"></div>`,
        });
        // Replace the marker's icon with the custom circle-like icon
        layer.setIcon(customIcon);
        // Mark as a secondary (simple dot) marker so hover behavior matches POI:
        // secondary markers get no hover outline. See useSelectMarkerOnMap.
        layer.options.simple = true;
        secondaryLayers.push(layer);
    }
}

const FavoriteLayer = () => {
    const ctx = useContext(AppContext);
    const ltx = useContext(LoginContext);

    const map = useMap();

    const [searchParams] = useSearchParams();

    const { lat } = useHashParams();
    const [move, setMove] = useState(false);
    const [zoom, setZoom] = useState(map ? map.getZoom() : 0);

    useZoomMoveMapHandlers(map, setZoom, setMove);

    const getFavoriteLayers = useCallback(
        () => Object.values(ctx.favorites?.mapObjs ?? {}).flatMap((file) => file.markers?.getLayers() ?? []),
        [ctx.favorites]
    );

    useSelectMarkerOnMap({ ctx, getLayers: getFavoriteLayers, type: FAVORITE_FILE_TYPE, map, zoom, move });

    // Creates a preview pin when user clicks the map to add a new favorite or track waypoint.
    useEffect(() => {
        const loc = ctx.addFavorite?.location;
        if (!loc || ctx.addFavorite?.editWpt || !map) return;

        applySelectedPin({
            ctx,
            map,
            layer: null,
            latlng: L.latLng(loc.lat, loc.lng),
            markerData: {
                color: DEFAULT_WPT_COLOR,
                background: MarkerOptions.BACKGROUND_WPT_SHAPE_CIRCLE,
                iconHtml: createPoiIcon({
                    color: DEFAULT_WPT_COLOR,
                    background: MarkerOptions.BACKGROUND_WPT_SHAPE_CIRCLE,
                    icon: ICONS_PREFIX + MarkerOptions.DEFAULT_WPT_ICON,
                }).options.html,
                invertIcon: false,
            },
            isSelection: true,
        });

        return () => resetSelectedPin({ ctx, map, force: true });
    }, [map, ctx.addFavorite?.location, ctx.addFavorite?.editWpt]);

    // Pan map to keep the preview pin in the visible center: on open, secondary drawer open/close.
    useEffect(() => {
        const req = ctx.addFavorite?.panRequest;
        if (!req || !map) return;
        panToVisibleCenter(map, getAddFavoritePinLatLng(ctx.addFavorite), req.infoBlockWidthPx);
    }, [ctx.addFavorite?.panRequest?.key]);

    // Updates the selected pin icon in real-time when user changes appearance (color, icon, shape).
    // Works for both add mode (preview pin) and edit mode (selected existing pin).
    useEffect(() => {
        const preview = ctx.addFavorite?.previewAppearance;
        if (!preview) {
            // Edit dialog closed — restore the selected-marker id if a wpt is still selected.
            const pin = ctx.selectedCreatedLayerRef?.current;
            if (pin && map.hasLayer(pin)) {
                const el = pin.getElement?.();
                const name = ctx.selectedWpt?.name;
                if (el && name) {
                    el.id = `se-selected-marker-${name}`;
                }
            }
            return;
        }

        const pin = ctx.selectedCreatedLayerRef?.current;
        if (!pin || !map.hasLayer(pin)) return;

        const iconHtml = createPoiIcon({
            color: preview.color,
            background: preview.background,
            icon: ICONS_PREFIX + preview.icon,
        }).options.html;

        const shape = toShape(preview.background);
        const color = preview.color?.startsWith('#') ? hexToRgba(preview.color) : (preview.color ?? DEFAULT_WPT_COLOR);

        pin.setIcon(
            createLayeredPinIcon({
                shape,
                color,
                iconHtml,
                size: SELECTED_PIN_SIZE,
                iconSize: SELECTED_ICON_SIZE,
            })
        );

        const el = pin.getElement?.();
        if (el) {
            const bg = String(preview.background ?? 'circle');
            const icon = String(preview.icon ?? '');
            const color = String(preview.color ?? '').replace(/^#/, '');
            el.id = `se-add-fav-map-preview--${bg}--${icon}--${color}`;
        }
    }, [ctx.addFavorite?.previewAppearance]);

    const openGroupId = useMemo(() => {
        const folderName = searchParams.get(FAVORITES_URL_PARAM_FOLDER);
        if (!folderName || !ctx.favorites?.groups) return null;
        const group = ctx.favorites.groups.find((g) => g.name === folderName);
        return group?.id || null;
    }, [searchParams, ctx.favorites?.groups]);

    const selectedGpxFileRef = useRef(ctx.selectedGpxFile);
    const folderNameRef = useRef(searchParams.get(FAVORITES_URL_PARAM_FOLDER));

    useEffect(() => {
        selectedGpxFileRef.current = ctx.selectedGpxFile;
    }, [ctx.selectedGpxFile]);

    useEffect(() => {
        folderNameRef.current = searchParams.get(FAVORITES_URL_PARAM_FOLDER);
    }, [searchParams]);

    useEffect(() => {
        if (ctx.removeFavGroup) {
            removeMarkersFromMap(ctx.removeFavGroup);
            ctx.setRemoveFavGroup(null);
        }
    }, [ctx.removeFavGroup]);

    useEffect(() => {
        if (!ltx.loginUser) {
            // If there is no logged-in user, remove all favorites layers.
            map.eachLayer((layer) => {
                if (layer.options.type === FAVORITE_FILE_TYPE) {
                    map.removeLayer(layer);
                }
            });
            deleteAllFavoritesFromDB().then();
        }
    }, [ltx.loginUser]);

    useEffect(() => {
        // created favorites markers and move them to ctx.favorites for adding to map
        if (ctx.updateMarkers) {
            ctx.setFavorites(() => {
                const favoritesGroups = { ...ctx.updateMarkers };
                favoritesGroups.mapObjs = Object.keys(favoritesGroups.mapObjs).reduce((group, key) => {
                    const file = favoritesGroups.mapObjs[key];
                    if (!file.markers) {
                        const fullGroup = TrackLayerProvider.createLayersByTrackData({
                            data: file,
                            groupId: key,
                            ctx,
                            map,
                            type: FAVORITE_FILE_TYPE,
                        });
                        const markerLayers = fullGroup ? fullGroup.getLayers().filter(isMarkerLayer) : [];
                        file.markers = new L.FeatureGroup(markerLayers);
                        if (ctx.selectedGpxFile?.markerCurrent && key === ctx.selectedGpxFile.id) {
                            updateSelectedFavoriteOnMap(file);
                        }
                    }
                    group[key] = file;
                    return group;
                }, {});
                ctx.setProcessingGroups(false);
                return favoritesGroups;
            });
        }
    }, [ctx.updateMarkers]);

    function removeMarkersFromMap(file, fileId) {
        deleteMarkers(file);
        deleteOldMarkers(file, fileId);
    }

    function updateMarkers({ onlyOpened = false } = {}) {
        const favoritesGroups = ctx.favorites?.mapObjs;
        if (!favoritesGroups) return;

        for (const fileId of Object.keys(favoritesGroups)) {
            const file = favoritesGroups[fileId];

            if (onlyOpened && !file.url) {
                continue;
            }

            const isInSearch = ctx.searchFavoriteGroupIds?.has(fileId) ?? false;
            if (!ctx.configureMapState.showFavorites && !openGroupId && !isInSearch) {
                removeMarkersFromMap(file, fileId);
                continue;
            }

            if (file.url) {
                if (!ctx.configureMapState.showFavorites && fileId !== openGroupId && !isInSearch) {
                    removeMarkersFromMap(file, fileId);
                } else {
                    addMarkersOnMap(file, fileId);
                }
                deleteOldMarkers(file, fileId);
            } else {
                removeMarkersFromMap(file, fileId);
            }
        }
    }

    function addMarkersOnMap(file, fileId) {
        const shouldShow =
            file.markers &&
            (file.hidden !== HIDDEN_TRUE || fileId === openGroupId || ctx.searchFavoriteGroupIds?.has(fileId));
        if (shouldShow) {
            if (file.markers) {
                const mapBounds = map.getBounds();

                const { mainMarkers, secondaryMarkers } = clusterMarkers({
                    places: filterPointsInBounds(file.wpts, map),
                    zoom,
                    latitude: lat,
                    iconSize: DEFAULT_ICON_SIZE,
                    isFavorites: true,
                });

                const markersToAdd = [];
                const mainLayers = [];
                const secondaryLayers = [];
                file.markers.eachLayer((layer) => {
                    if (!layer) return;

                    const markerLatLng = layer.getLatLng?.();
                    if (!markerLatLng) return;

                    if (!mapBounds.contains(markerLatLng)) {
                        return;
                    }
                    layer.options.isFavorite = true;
                    if (!layer.options.hasClickHandler) {
                        layer.on('click', onClick);
                        layer.options.hasClickHandler = true;
                    }
                    processMarkers({
                        layer,
                        markerLatLng,
                        mainMarkers,
                        secondaryMarkers,
                        mainLayers,
                        secondaryLayers,
                    });
                });
                markersToAdd.push(...mainLayers, ...secondaryLayers);

                markersToAdd.forEach((marker) => {
                    addMarkerTooltip({
                        marker,
                        setSelectedId: ctx.setSelectedWptId,
                        type: FAVORITE_FILE_TYPE,
                        mainStyle: true,
                        text: marker.options['name'],
                        latlng: marker._latlng,
                        iconSize: DEFAULT_ICON_SIZE,
                        map,
                        ctx,
                    });
                });

                if (file.markersOnMap) {
                    map.removeLayer(file.markersOnMap);
                }

                const mainLayersGroup = new L.FeatureGroup(mainLayers);
                const secLayersGroup = new L.FeatureGroup(secondaryLayers);
                const res = new L.LayerGroup([secLayersGroup, mainLayersGroup]);
                res.addTo(map);
                // When a hidden favorites group is opened explicitly, keep all its markers visible.
                if (fileId === openGroupId) {
                    // Opened from favorites menu — show all markers in the group.
                    [...mainLayers, ...secondaryLayers].forEach((marker) => {
                        if (marker?._icon?.style) {
                            marker._icon.style.display = '';
                        }
                    });
                } else if (ctx.searchFavoriteGroupIds?.has(fileId)) {
                    // Opened from search — show only the specific markers that matched the query.
                    const matchedNames = ctx.searchFavoriteGroupIds.get(fileId);
                    [...mainLayers, ...secondaryLayers].forEach((marker) => {
                        if (marker?._icon?.style && matchedNames?.has(marker.options?.name)) {
                            marker._icon.style.display = '';
                        }
                    });
                }
                updateMarkerZIndex(mainLayersGroup, MARKER_Z_INDEX_MAIN);
                file.markersOnMap = res;
                // Re-hide markers under the selected pin after new markers are placed on the map
                if (ctx.selectedCreatedLayerRef?.current && map.hasLayer(ctx.selectedCreatedLayerRef.current)) {
                    hideMarkersUnderPin(map, ctx);
                }
            }
        } else {
            if (file.markersOnMap) {
                map.removeLayer(file.markersOnMap);
            }
        }
    }

    useEffect(() => {
        ctx.setFavLoading(false);
        updateMarkers();
    }, [ctx.favorites, openGroupId, ctx.searchFavoriteGroupIds, ctx.configureMapState.showFavorites]);

    // update markers on map after zoom
    useEffect(() => {
        updateMarkers({ onlyOpened: true });
    }, [zoom]);

    // update markers on map after move
    useEffect(() => {
        if (move) {
            updateMarkers({ onlyOpened: true });
            setMove(false);
        }
    }, [move]);

    useEffect(() => {
        if (!ctx.configureMapState.showFavorites && !openGroupId) return;

        const current = ctx.selectedGpxFile?.markerCurrent;
        if (!current?.name) return;

        panToIfNeeded({ map, latlng: current.latlng, ctx });
    }, [ctx.selectedGpxFile?.id]);

    const onClick = useCallback(
        (e) => {
            if (globalThis.location.pathname.includes(NAVIGATE_URL)) {
                const lat = e.latlng?.lat ?? e.sourceTarget?._latlng?.lat;
                const lon = e.latlng?.lng ?? e.sourceTarget?._latlng?.lng;

                if (lat && lon) {
                    const wpt = {
                        ...e.sourceTarget.options,
                        lat,
                        lng: lon,
                        type: NAVIGATION_OBJECT_TYPE_FAVORITE,
                    };
                    const navEvent = new CustomEvent('nav-marker-click', {
                        detail: {
                            wpt,
                            latlng: L.latLng(lat, lon),
                        },
                        cancelable: true,
                    });
                    navEvent.originalEvent = e.originalEvent;
                    globalThis.dispatchEvent(navEvent);

                    // If NavigationLayer handled it (found empty input), don't open info
                    if (navEvent.defaultPrevented) {
                        L.DomEvent.stopPropagation(e);
                        return;
                    }
                }
            }

            // Normal logic: open context info block
            ctx.selectedGpxFile = {};
            ctx.selectedGpxFile.trackData = { ...ctx.favorites.mapObjs[e.sourceTarget.options.groupId] };

            const groupWithOriginalFile = ctx.favorites.groups?.find((g) => g.id === e.sourceTarget.options.groupId);
            ctx.selectedGpxFile.file = groupWithOriginalFile.file;

            ctx.selectedGpxFile.prevState = selectedGpxFileRef.current;
            ctx.selectedGpxFile.markerCurrent = {
                name: e.sourceTarget.options.name,
                point: e.sourceTarget.options.point,
                pointIndex: ctx.selectedGpxFile.trackData.wpts?.indexOf(e.sourceTarget.options.point),
                iconHtml: e.sourceTarget.options.originalIcon?.options?.html,
                iconName: e.sourceTarget.options.iconName,
                color: e.sourceTarget.options.color,
                background: e.sourceTarget.options.background,
                groupId: e.sourceTarget.options.groupId,
                iconSize: e.sourceTarget.options.icon?.options?.iconSize?.[0],
                layer: e.sourceTarget,
                latlng: e.sourceTarget.getLatLng(),
            };
            ctx.selectedGpxFile.name = ctx.selectedGpxFile.markerCurrent.name;
            ctx.selectedGpxFile.nameGroup = e.sourceTarget.options.category
                ? e.sourceTarget.options.category
                : FavoritesManager.DEFAULT_GROUP_NAME;
            ctx.selectedGpxFile.id = e.sourceTarget.options.groupId;
            ctx.selectedGpxFile.key = `${ctx.selectedGpxFile.id}:${ctx.selectedGpxFile.name}`;

            ctx.selectedGpxFile.mapObj = true;
            ctx.selectedGpxFile.openedFolder = folderNameRef.current ?? undefined;

            ctx.setSelectedFavoriteObj(null);

            openFavoriteObj({
                ctx,
                favoriteObj: ctx.selectedGpxFile,
                options: { fromSearch: isFavoriteFromSearch(ctx) },
            });
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
        },
        [ctx, selectedGpxFileRef]
    );

    function updateSelectedFavoriteOnMap(file) {
        const point = findSelectedWpt(ctx.selectedGpxFile.markerCurrent, file?.wpts);
        const layer = Object.values(file?.markers?._layers ?? {}).find((m) => m.options?.point === point);
        if (!layer) return;

        // Update the selected pin icon in-place with the new saved appearance.
        const existingPin = ctx.selectedCreatedLayerRef?.current;
        if (existingPin && map.hasLayer(existingPin)) {
            const opts = layer.options;
            const iconHtml = opts.icon?.options?.html ?? '';
            const shape = toShape(opts.background);
            const color = opts.color?.startsWith('#') ? hexToRgba(opts.color) : (opts.color ?? DEFAULT_WPT_COLOR);
            existingPin.setIcon(
                createLayeredPinIcon({
                    shape,
                    color,
                    iconHtml,
                    size: SELECTED_PIN_SIZE,
                    iconSize: SELECTED_ICON_SIZE,
                })
            );
        }

        const opts = layer.options;
        const iconOpts = opts.icon?.options;
        ctx.selectedGpxFile.markerPrev = { ...ctx.selectedGpxFile.markerCurrent };
        ctx.selectedGpxFile.markerCurrent = {
            ...ctx.selectedGpxFile.markerCurrent,
            layer,
            point,
            pointIndex: file.wpts.indexOf(point),
            icon: iconOpts?.html,
            color: opts.color,
            background: opts.background,
            groupId: opts.groupId,
            latlng: layer.getLatLng(),
            originalIconHtml: opts.originalIcon?.options?.html,
        };
        ctx.setSelectedGpxFile({ ...ctx.selectedGpxFile });
    }

    function deleteMarkers(file) {
        if (file.markers && !isEmpty(file.markers)) {
            if (map.hasLayer(file.markers)) {
                map.removeLayer(file.markers);
            }
        }
        if (file.markersOnMap) {
            map.removeLayer(file.markersOnMap);
        }
    }

    function deleteOldMarkers(file, fileId) {
        if (file?.oldMarkers) {
            map.removeLayer(file.oldMarkers);
            delete file.oldMarkers;
        }
        if (
            file?.hidden === HIDDEN_TRUE &&
            file?.markersOnMap &&
            fileId !== openGroupId &&
            !ctx.searchFavoriteGroupIds?.has(fileId)
        ) {
            map.removeLayer(file.markersOnMap);
        }
    }

    return null;
};

export default FavoriteLayer;
